// Splits a WEEKEND activity group that's grown too big into one sub-group per
// staff member (an adult member who's on the activities/leaders list and on duty
// today). Students are distributed AS EVENLY AS POSSIBLE across the leader's
// group and each staff sub-group; within that even split we keep booking groups
// together first, then individuals (students with no booking group). Non-staff
// adults (group GLs etc.) all stay with the original leader.

const WEEKEND = "WEEKEND";
const MIN_STUDENTS_TO_SPLIT = 10;

const norm = (v) => String(v ?? "").trim();
const isTrue = (v) => v === 1 || v === true;
const isAdult = (m) => norm(m?.personType).toUpperCase() === "ADULT";
const isStudent = (m) => norm(m?.personType).toUpperCase() === "STUDENT";

function groupProductType(group, products) {
  const t = norm(group?.productType).toUpperCase();
  if (t) return t;
  const linked = (products || []).find(
    (p) => String(p.productId) === String(group?.productId)
  );
  return norm(linked?.productType).toUpperCase();
}

function leadersByAdultId(leaders) {
  const map = new Map();
  (leaders || []).forEach((l) => {
    if (l?.adultId != null) map.set(String(l.adultId), l);
  });
  return map;
}

// Even split: base count in every bin, with the remainder going to the earlier
// bins (so 35 over 4 → [9, 9, 9, 8]).
function evenBinSizes(total, bins) {
  const base = Math.floor(total / bins);
  const rem = total % bins;
  return Array.from({ length: bins }, (_, i) => base + (i < rem ? 1 : 0));
}

// Fill fixed-size bins with student chunks: place each booking-group chunk whole
// where it fits (tightest bin first), split any chunk too big for a single bin
// contiguously, then fill the remaining gaps with individuals last.
function fillBins(binSizes, bookingChunks, individuals) {
  const bins = binSizes.map((cap) => ({ cap, students: [] }));
  const remaining = (b) => b.cap - b.students.length;

  const oversized = [];
  [...bookingChunks]
    .sort((a, b) => b.length - a.length)
    .forEach((chunk) => {
      let best = -1;
      let bestRem = Infinity;
      bins.forEach((b, i) => {
        const r = remaining(b);
        if (r >= chunk.length && r < bestRem) {
          best = i;
          bestRem = r;
        }
      });
      if (best >= 0) bins[best].students.push(...chunk);
      else oversized.push(chunk);
    });

  // Fill the emptiest bin fully, then the next — keeps a split chunk contiguous.
  const placeContiguous = (queue) => {
    let rest = [...queue];
    while (rest.length) {
      let target = -1;
      let maxRem = 0;
      bins.forEach((b, i) => {
        const r = remaining(b);
        if (r > maxRem) {
          maxRem = r;
          target = i;
        }
      });
      if (target < 0) break;
      bins[target].students.push(...rest.splice(0, Math.min(maxRem, rest.length)));
    }
  };

  oversized.sort((a, b) => b.length - a.length).forEach(placeContiguous);
  placeContiguous(individuals);

  return bins.map((b) => b.students);
}

// Returns the split plan, or null when the group isn't eligible (not WEEKEND, ≤10
// students, or no on-duty staff members to split out).
export function planWeekendSplit({ group, products = [], leaders = [] }) {
  if (!group) return null;
  if (groupProductType(group, products) !== WEEKEND) return null;

  const members = Array.isArray(group.members) ? group.members : [];
  const students = members.filter(isStudent);
  if (students.length <= MIN_STUDENTS_TO_SPLIT) return null;

  const byAdult = leadersByAdultId(leaders);
  const isExistingLeader = (m) =>
    String(m.personId) === String(group.leaderAdultId) ||
    String(m.personId) === String(group.leaderStaffId);

  // Staff = adult members on the leaders list, on duty, not the current leader.
  const staffMembers = members.filter((m) => {
    if (!isAdult(m) || isExistingLeader(m)) return false;
    const entry = byAdult.get(String(m.personId));
    return entry != null && isTrue(entry.scheduledToday);
  });
  if (staffMembers.length === 0) return null;

  const staffIds = new Set(staffMembers.map((m) => String(m.personId)));
  const otherAdults = members.filter(
    (m) => isAdult(m) && !staffIds.has(String(m.personId))
  );

  const sortedStaff = [...staffMembers].sort((a, b) =>
    norm(a.name).localeCompare(norm(b.name))
  );

  const binSizes = evenBinSizes(students.length, 1 + sortedStaff.length);

  // Chunks: one per booking group, plus a single individuals chunk (no booking).
  const byBooking = new Map();
  const individuals = [];
  students.forEach((s) => {
    const bg = norm(s.bookingGroupName);
    if (bg) {
      if (!byBooking.has(bg)) byBooking.set(bg, []);
      byBooking.get(bg).push(s);
    } else {
      individuals.push(s);
    }
  });

  const bins = fillBins(binSizes, Array.from(byBooking.values()), individuals);
  const toRef = (m) => ({ personType: "STUDENT", personId: m.personId });

  const leaderBin = bins[0] || [];
  const staffBins = bins.slice(1);

  const subGroups = sortedStaff.map((staff, i) => ({
    staffId: byAdult.get(String(staff.personId))?.staffId ?? null,
    staffName: norm(staff.name),
    students: (staffBins[i] || []).map(toRef),
  }));

  // Largest resulting group — used to warn (not block) when there aren't enough
  // staff to keep every group at 10 students or fewer.
  const maxGroupSize = Math.max(
    leaderBin.length,
    ...subGroups.map((s) => s.students.length)
  );

  const originalMembers = [
    ...otherAdults.map((m) => ({ personType: "ADULT", personId: m.personId })),
    ...leaderBin.map(toRef),
  ];

  return {
    groupId: group.groupId,
    subGroups,
    originalMembers,
    summary: {
      leaderName:
        group.leaderName ||
        byAdult.get(String(group.leaderAdultId))?.name ||
        "Current leader",
      leaderAdults: otherAdults.length,
      leaderStudents: leaderBin.length,
      subs: subGroups.map((s) => ({ name: s.staffName, students: s.students.length })),
      maxGroupSize,
    },
  };
}

export function canSplitWeekendGroup(args) {
  return planWeekendSplit(args) != null;
}
